import { NO_DATE, formatDate, formatDateTime } from './datetime'

/**
 * ตัวจัดรูปแบบของหน้ารายงาน "ข้อมูลบุคคล" — ฟังก์ชันล้วน ไม่แตะ React
 *
 * แยกออกจาก person.ts โดยตั้งใจ: person.ts import React กับ SessionContext
 * จึงรันนอกเบราว์เซอร์ไม่ได้ ส่วนไฟล์นี้ทดสอบตรง ๆ ด้วย
 *   node --experimental-strip-types <test>
 * ได้เลย — ตรรกะช่วงวันที่มี 5 ทางแยก ตรวจด้วยตาแล้วพลาดง่าย
 *
 * ⚠️ **รูปแบบวันที่ทั้งหมดต้องมาจาก lib/datetime.ts เท่านั้น** ห้ามตัดสตริงเอง
 *    ไฟล์นั้นเขียนกติกาไว้ว่า `YYYY-MM-DD` และ `YYYY-MM-DD HH:mm` ค.ศ. เท่านั้น
 *    พร้อมเหตุผลว่าทำไมห้ามใช้ toLocaleDateString (locale ไทยแปลงเป็น พ.ศ. ให้เอง
 *    โดยที่โค้ดไม่ได้สั่ง แล้วแต่ละเครื่องได้ผลไม่เหมือนกัน)
 *    เคยพลาดมาแล้วที่นี่: เขียน dd/MM/yyyy ขึ้นเองแล้วหน้าเดียวกันมีสองรูปแบบ
 *
 * person.ts re-export ทุกตัวในนี้ต่อ หน้าจอจึง import จากที่เดียวเหมือนเดิม
 */

export const MONTH_TH = [
  'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
  'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.',
]

const DOW_TH = ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส']
export const dowText = (dow: number) => DOW_TH[dow] ?? ''

/** GUID/UUID เปล่า ๆ — รับทั้งแบบมีและไม่มีปีกกาครอบ */
const UUID_ONLY = /^\{?[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\}?$/i

/**
 * ค่าที่เอาไว้ให้ "คนอ่าน" — คืน null ถ้าเป็นขยะที่ระบบเขียนทิ้งไว้
 *
 * ของจริงในตาราง Users มีช่อง LastName_Eng ที่กลายเป็น GUID ไปทั้งช่อง
 * (รหัส 0920 = `5fffe10d-15e6-473e-a72c-1e1f257b04de`) ซึ่งไม่ใช่ชื่อคนแน่ ๆ
 *
 * ⚠️ นี่คือการ **ซ่อนตอนแสดงผล ไม่ใช่การลบข้อมูล** — เจ้าของระบบสั่งไว้ว่าไม่ลบ
 *    ค่าใน DB ยังอยู่ครบ และหน้า /admin/employees ยัง**แสดงค่าดิบตามเดิม**
 *    เพื่อให้ HR เห็นและแก้ได้ ถ้าซ่อนทั้งสองที่จะไม่มีใครรู้ว่ามีข้อมูลเสียอยู่
 *
 * พ่วงงานเก็บกวาดเล็กน้อย: ยุบช่องว่างซ้อนให้เหลือช่องเดียว (ของจริงมี
 * `Nussara   Yenstid` เว้น 3 ช่อง) — แค่เรื่องการแสดงผล ไม่แตะค่าต้นทาง
 */
export function humanText(v: string | null | undefined): string | null {
  if (!v) return null
  const s = String(v).trim().replace(/\s+/g, ' ')
  if (!s || UUID_ONLY.test(s)) return null
  return s
}

export type LeaveRange = {
  /** คอลัมน์ "ตั้งแต่" — `YYYY-MM-DD HH:mm` หรือ NO_DATE */
  from: string
  /** คอลัมน์ "ถึง" — `YYYY-MM-DD HH:mm` หรือ NO_DATE */
  to: string
  /** ใบนี้กินหลายวันไหม — ใช้ติดป้ายให้สังเกตง่าย */
  multiDay: boolean
}

/**
 * ช่วงวันที่ของใบลา/ใบปรับปรุงเวลา — ข้อมูลจริงมี 5 แบบ ต้องรองรับให้หมด
 * (SM01 ปี 2026: มีทั้งสองวัน 1,930 · มีแต่วันเริ่ม 72 · มีแต่วันจบ 198 · ข้ามวัน 128)
 *
 *                    ตั้งแต่              ถึง
 *   วันเดียว        2026-02-11 08:30    2026-02-11 12:30
 *   ข้ามวัน         2026-03-06 08:30    2026-03-09 17:30
 *   มีแต่เวลาเข้า   2026-04-10 08:30    -                  ← ใบแก้เฉพาะเวลาเข้า
 *   มีแต่เวลาออก    -                   2026-01-14 19:00   ← ใบแก้เฉพาะเวลาออก
 *   ไม่มีวันเลย     2026-05-02          -                  ← ตกกลับไปใช้วันของใบ
 *
 * ⚠️ ห้ามย่อวันที่เป็น dd/MM ละปีทิ้ง หรือละวันในคอลัมน์ "ถึง" ตอนเป็นวันเดียวกัน
 *    แม้จะสั้นกว่า — กติกาของทั้งระบบคือ YYYY-MM-DD (ดูหัวไฟล์ lib/datetime.ts)
 *    และคอลัมน์ที่บางแถวมีวันบางแถวไม่มี อ่านทีละคอลัมน์แล้วแปลผิดได้
 *
 * ⚠️ ห้ามตัดเวลาทิ้งในใบข้ามวัน — ตรวจของจริงแล้วมีแค่ 54/128 ใบที่เป็นกะมาตรฐาน
 *    08:30–17:30 ที่เหลือเป็นกะดึกข้ามเที่ยงคืน (23:30–08:30, 15:00–00:00,
 *    16:00–01:00) ซึ่งเวลาคือสาระสำคัญของใบ
 *
 * ⚠️ ห้ามใช้ `workDate` เป็นวันของช่วงถ้ามี dateStart/dateEnd — ตรวจกับของจริงแล้ว
 *    มันคือ **วันที่ยื่นใบ** ไม่ใช่วันที่ลา (เจอใบลาพักร้อน dateStart 2026-04-06
 *    แต่ workDate 2026-03-23 คนละเดือนกัน)
 */
export function leaveRange(f: {
  dateStart: string | null
  dateEnd: string | null
  workDate: string | null
}): LeaveRange {
  const { dateStart: s, dateEnd: e } = f

  // ไม่มีทั้งสองฝั่ง — ยังไม่เจอในข้อมูลจริง (SM01 ปี 2026 ไม่มีสักใบ) แต่กันไว้
  if (!s && !e) {
    return { from: f.workDate ? formatDate(f.workDate) : NO_DATE, to: NO_DATE, multiDay: false }
  }

  // ใบปรับปรุงเวลาที่แก้ด้านเดียว — ปล่อยอีกคอลัมน์เป็น NO_DATE ไปเลย
  // สองคอลัมน์บอกเองได้ว่าขาดฝั่งไหน ไม่ต้องเขียน "เข้า"/"ออก" กำกับ
  if (s && !e) return { from: formatDateTime(s), to: NO_DATE, multiDay: false }
  if (!s && e) return { from: NO_DATE, to: formatDateTime(e), multiDay: false }

  const start = s as string
  const end = e as string
  return {
    from: formatDateTime(start),
    to: formatDateTime(end),
    multiDay: formatDate(start) !== formatDate(end),
  }
}

/**
 * ตัวเลขวัน — ตัดทศนิยมที่ไม่จำเป็นออก (5.63 คงไว้ · 6.00 เป็น 6)
 * ค่าลาเป็นเศษส่วนของวันจริง ๆ (ลาครึ่งวัน = 0.5) จึงปัดทิ้งไม่ได้
 */
export function dayNum(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—'
  return String(Math.round(v * 100) / 100)
}

/**
 * 1 วันลา = 8 ชั่วโมง — ยืนยันจากข้อมูลจริง (ลาเต็มวัน 08:30–17:30 ได้ 8 ชม.
 * คือหักพักเที่ยงแล้ว) ค่าเดียวกับ HOURS_PER_DAY ใน backend/src/lib/leave.js
 */
const HOURS_PER_DAY = 8
const MIN_PER_DAY = HOURS_PER_DAY * 60

/**
 * จำนวนวันลา → `1 วัน 3 ชม. 31 น.`
 *
 * เลขทศนิยมอย่าง `1.44 วัน` อ่านแล้วแปลงในหัวไม่ออกว่ากี่ชั่วโมง (0.44 วัน ≠ 4 ชม.
 * เพราะฐานเป็น 8 ไม่ใช่ 10) ระบบ HR เดิมก็แปลงเป็น วัน:ชม:นาที ด้วยเหตุผลเดียวกัน
 * แต่ใช้รูป `1:03:31` ซึ่งอ่านสับสนกับเวลานาฬิกา — ที่นี่เขียนหน่วยกำกับให้ชัด
 *
 * ตัดหน่วยที่เป็นศูนย์ทิ้ง (`8 วัน 2 ชม.` ไม่ใช่ `8 วัน 2 ชม. 0 น.`)
 * ⚠️ รองรับค่าติดลบ — คงเหลือติดลบได้จริงเมื่อใช้เกินสิทธิ์
 */
export function dayHourMin(days: number | null | undefined): string {
  if (days === null || days === undefined) return '—'

  const total = Math.round(days * MIN_PER_DAY)
  if (total === 0) return '0 วัน'

  const abs = Math.abs(total)
  const d = Math.floor(abs / MIN_PER_DAY)
  const h = Math.floor((abs % MIN_PER_DAY) / 60)
  const m = abs % 60

  const parts: string[] = []
  if (d) parts.push(`${d} วัน`)
  if (h) parts.push(`${h} ชม.`)
  if (m) parts.push(`${m} น.`)
  return `${total < 0 ? '-' : ''}${parts.join(' ')}`
}

/** นาที → `1 ชม. 20 น.` — เลขนาทีดิบอ่านยากเมื่อเกินชั่วโมง */
export function minutesText(m: number): string {
  if (!m) return '—'
  if (m < 60) return `${m} น.`
  const h = Math.floor(m / 60)
  const rest = m % 60
  return rest ? `${h} ชม. ${rest} น.` : `${h} ชม.`
}

export function errorText(err: unknown): string {
  return err instanceof Error ? err.message : String(err)
}
